import type { ApiClient, CallArgs, Endpoint, EndpointResponse } from '@croissants/contracts';
import { queryKeyFor } from '@croissants/ui';
import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useApi } from './api';

/**
 * Starting a read BEFORE the component that renders it exists.
 *
 * Two places in this portal know a read's arguments earlier than its reader
 * does. A record screen holds the id in its URL, while the panels under it
 * mount only once the record itself has answered - so every panel's read was a
 * second wave, one full round trip after the first (portal speed plan, 23 Oct
 * 2026, section 5a). And the rail knows where the operator is about to go the
 * moment the pointer reaches an entry, a few hundred milliseconds before the
 * click mounts the screen.
 *
 * Both warm the SAME cache entry the reader's own hook then reads: the key is
 * `queryKeyFor`, the one `useEndpointQuery` and `useEndpointPages` build, and
 * the fetch is the same `client.call`. The arguments must therefore be the
 * reader's EXACTLY - a prefetch under different arguments is one wasted request
 * and nothing worse, because the reader still asks for its own. A fresh entry
 * (the portal's 30 s `staleTime`) is not fetched again, so hovering an entry
 * twice, or a panel mounting after its prefetch, costs nothing.
 *
 * `pages` is for a reader that uses `useEndpointPages`: its cache entry is the
 * `{ pages, pageParams }` shape, and writing a plain answer under that key
 * would hand the infinite query a value it cannot read. A fresh entry gets the
 * first page, which is what the reader's own first fetch is; `pageAt` is the
 * reader's own, so a stale entry holding several pages is walked the same way
 * the reader would walk it.
 */
export interface Prefetcher {
  query<E extends Endpoint>(endpoint: E, args: CallArgs<E>): void;
  pages<E extends Endpoint>(
    endpoint: E,
    args: CallArgs<E>,
    pageAt?: (response: EndpointResponse<E>) => { readonly nextCursor: string | null },
  ): void;
}

export function prefetcherFor(queryClient: QueryClient, client: ApiClient): Prefetcher {
  return {
    query(endpoint, args) {
      void queryClient.prefetchQuery({
        queryKey: queryKeyFor(endpoint, args),
        queryFn: ({ signal }) => client.call(endpoint, { ...args, signal }),
      });
    },
    pages<E extends Endpoint>(
      endpoint: E,
      args: CallArgs<E>,
      pageAt?: (response: EndpointResponse<E>) => { readonly nextCursor: string | null },
    ) {
      const shape = args as { query?: Record<string, unknown> };
      void queryClient.prefetchInfiniteQuery<
        EndpointResponse<E>,
        Error,
        EndpointResponse<E>,
        readonly unknown[],
        string | undefined
      >({
        queryKey: queryKeyFor(endpoint, args),
        queryFn: ({ pageParam, signal }) =>
          client.call(endpoint, {
            ...args,
            ...(pageParam === undefined
              ? {}
              : { query: { ...(shape.query ?? {}), cursor: pageParam } }),
            signal,
          } as CallArgs<E>),
        initialPageParam: undefined,
        getNextPageParam: (last: EndpointResponse<E>) =>
          (pageAt === undefined ? (last as { readonly nextCursor: string | null }) : pageAt(last))
            .nextCursor ?? undefined,
      });
    },
  };
}

export function usePrefetch(): Prefetcher {
  const queryClient = useQueryClient();
  const client = useApi().client;
  return useMemo(() => prefetcherFor(queryClient, client), [queryClient, client]);
}
